import React, { useEffect, useRef, useState } from 'react';
import TeamMemberTag, { MemberTagItem } from 'components/common/TeamMemberTag';

interface MemberTagListProps {
  items: MemberTagItem[];
  onRemove: (id: string) => void;
  /** custom avatar slot for each tag (companies use an icon instead of initials) */
  renderLeading?: (item: MemberTagItem) => React.ReactNode;
  className?: string;
}

/**
 * Shows the first selected item as a pill; everything else collapses into a
 * "+N more" pill that opens a popover with the remaining removable pills.
 */
const MemberTagList: React.FC<MemberTagListProps> = ({
  items,
  onRemove,
  renderLeading,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [first, ...rest] = items;

  // close the popover once the overflow list runs out
  useEffect(() => {
    if (rest.length === 0) setIsOpen(false);
  }, [rest.length]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // the popover can open below the fold of a scrollable form/modal; scroll the
  // minimum needed once it has rendered (no-ops when it's already visible)
  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  if (items.length === 0) return null;

  return (
    <div ref={wrapperRef} className={`relative flex flex-wrap items-center gap-2 ${className}`}>
      <TeamMemberTag
        item={first}
        onRemove={onRemove}
        leading={renderLeading?.(first)}
      />

      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 transition-colors ${
            isOpen
              ? 'border-emphasis bg-[#252B3B]'
              : 'border-primary-subtle bg-[#252B3B] hover:border-neutral-medium'
          }`}
        >
          <span className="text-sm text-neutral">+{rest.length}</span>
        </button>
      )}

      {isOpen && rest.length > 0 && (
        <div
          ref={panelRef}
          className="absolute left-0 top-full z-20 mt-2 w-[250px] sm:w-[350px] rounded-xl border border-primary-subtle bg-[#1A1F2C] p-3 shadow-2xl"
        >
          <div className="flex max-h-[180px] flex-col sm:flex-row sm:flex-wrap gap-2 overflow-y-auto">
            {rest.map((item) => (
              <div className='w-fit'>
                 <TeamMemberTag
                    key={item.id}
                    item={item}
                    onRemove={onRemove}
                    leading={renderLeading?.(item)}
                  />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberTagList;
